import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FiArrowLeft, FiUser, FiClock, FiDollarSign, FiCreditCard,
  FiCheckCircle, FiXCircle, FiAlertCircle, FiRefreshCw,
  FiCalendar, FiTrendingUp, FiPlay
} from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';
import SafeIcon from '@/components/SafeIcon';

interface UserBillingTimelineProps {
  userId: string;
  onBack: () => void;
}

interface TimelineData {
  profile: {
    email: string;
    name: string;
    subscription_plan_id: string | null;
    billing_status: string | null;
    billing_provider: string | null;
    paid_until: string | null;
    is_in_trial: boolean;
    trial_ends_at: string | null;
    stripe_customer_id: string | null;
    created_at: string;
  };
  events: Array<{
    id: string;
    event_type: string;
    event_source: string;
    plan_id: string | null;
    previous_state: string | null;
    new_state: string | null;
    payment_method: string | null;
    amount_usd: number | null;
    invoice_id: string | null;
    tx_hash: string | null;
    metadata: Record<string, unknown>;
    actor_type: string | null;
    is_anomaly: boolean | null;
    anomaly_type: string | null;
    created_at: string;
  }>;
  invoices: Array<{
    id: string;
    plan_id: string;
    amount_usd: number;
    crypto_currency: string;
    crypto_amount: number | null;
    status: string;
    tx_hash: string | null;
    created_at: string;
    paid_at: string | null;
  }>;
  stats: {
    total_paid_usd: number;
    total_invoices: number;
    anomaly_count: number;
  };
}

const UserBillingTimeline: React.FC<UserBillingTimelineProps> = ({ userId, onBack }) => {
  const [data, setData] = useState<TimelineData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        const { data: result, error: rpcError } = await supabase.rpc('get_user_billing_timeline', {
          p_user_id: userId
        });

        if (rpcError) throw rpcError;
        if (!result.success) throw new Error(result.error);
        
        setData(result as TimelineData);
      } catch (err) {
        console.error('Error fetching timeline:', err);
        setError(err instanceof Error ? err.message : 'Failed to load timeline');
      } finally {
        setLoading(false);
      }
    };

    fetchTimeline();
  }, [userId]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'active': return 'text-green-400 bg-green-500/10';
      case 'trialing': return 'text-cyan-400 bg-cyan-500/10';
      case 'past_due': return 'text-yellow-400 bg-yellow-500/10';
      case 'canceled':
      case 'expired': return 'text-red-400 bg-red-500/10';
      default: return 'text-gray-400 bg-gray-500/10';
    }
  };

  const getEventIcon = (eventType: string) => {
    if (eventType.includes('payment') || eventType.includes('invoice')) return FiDollarSign;
    if (eventType.includes('trial')) return FiPlay;
    if (eventType.includes('upgrade')) return FiTrendingUp;
    if (eventType.includes('expired') || eventType.includes('failed')) return FiXCircle;
    if (eventType.includes('success') || eventType.includes('confirm')) return FiCheckCircle;
    return FiClock;
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <SafeIcon icon={FiRefreshCw} className="mx-auto text-gray-500 animate-spin" size={32} />
        <p className="mt-2 text-gray-400">Loading billing timeline...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-8 text-center">
        <SafeIcon icon={FiAlertCircle} className="mx-auto text-red-400" size={32} />
        <p className="mt-2 text-red-400">{error || 'Failed to load data'}</p>
        <button onClick={onBack} className="mt-4 text-gray-400 hover:text-white flex items-center gap-2 mx-auto">
          <SafeIcon icon={FiArrowLeft} size={16} /> Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="p-2 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 transition-colors">
          <SafeIcon icon={FiArrowLeft} className="text-gray-300" size={20} />
        </button>
        <div>
          <h2 className="text-xl font-semibold text-white">User Billing Timeline</h2>
          <p className="text-sm text-gray-400">{data.profile.email}</p>
        </div>
      </div>

      {/* User Profile Card */}
      <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-cyan-600 flex items-center justify-center">
            <SafeIcon icon={FiUser} className="text-white" size={24} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-white">{data.profile.name || data.profile.email}</h3>
            <p className="text-sm text-gray-400">{data.profile.email}</p>
            
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase">Status</p>
                <span className={`inline-block mt-1 px-2 py-1 rounded text-xs font-medium ${getStatusColor(data.profile.billing_status)}`}>
                  {data.profile.billing_status || 'No Status'}
                </span>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Plan</p>
                <p className="text-sm text-white mt-1">{data.profile.subscription_plan_id || 'None'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Provider</p>
                <p className="text-sm text-white mt-1 capitalize">{data.profile.billing_provider || 'None'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Paid Until</p>
                <p className="text-sm text-white mt-1">
                  {data.profile.paid_until ? formatDate(data.profile.paid_until) : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Stats */}
        <div className="mt-6 pt-6 border-t border-gray-700/50 grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-green-400">${data.stats.total_paid_usd}</p>
            <p className="text-xs text-gray-500 uppercase">Total Paid</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-cyan-400">{data.stats.total_invoices}</p>
            <p className="text-xs text-gray-500 uppercase">Total Invoices</p>
          </div>
          <div className="text-center">
            <p className={`text-2xl font-bold ${data.stats.anomaly_count > 0 ? 'text-red-400' : 'text-gray-400'}`}>
              {data.stats.anomaly_count}
            </p>
            <p className="text-xs text-gray-500 uppercase">Anomalies</p>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700/50">
          <h3 className="text-lg font-semibold text-white">Event Timeline</h3>
        </div>
        
        <div className="max-h-[600px] overflow-y-auto">
          {data.events.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No events recorded</div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 top-0 bottom-0 w-px bg-gray-700/50" />
              
              {data.events.map((event, index) => {
                const Icon = getEventIcon(event.event_type);
                const isAnomaly = event.is_anomaly;
                
                return (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`relative px-6 py-4 hover:bg-gray-700/20 ${isAnomaly ? 'bg-red-500/5' : ''}`}
                  >
                    {/* Timeline dot */}
                    <div className={`absolute left-6 w-5 h-5 rounded-full flex items-center justify-center ${
                      isAnomaly ? 'bg-red-500/20' : 'bg-gray-700'
                    }`}>
                      <SafeIcon icon={Icon} className={isAnomaly ? 'text-red-400' : 'text-gray-400'} size={12} />
                    </div>
                    
                    <div className="ml-10">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-white">
                          {event.event_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        <span className="px-2 py-0.5 rounded text-xs bg-gray-700/50 text-gray-400">
                          {event.event_source}
                        </span>
                        {event.plan_id && (
                          <span className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400">
                            {event.plan_id}
                          </span>
                        )}
                        {isAnomaly && (
                          <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400">
                            <SafeIcon icon={FiAlertCircle} className="inline mr-1" size={10} />
                            {event.anomaly_type}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <SafeIcon icon={FiClock} size={10} />
                          {formatDate(event.created_at)}
                        </span>
                        {event.amount_usd && (
                          <span className="flex items-center gap-1 text-green-400">
                            <SafeIcon icon={FiDollarSign} size={10} />
                            ${event.amount_usd}
                          </span>
                        )}
                        {event.payment_method && (
                          <span className="flex items-center gap-1">
                            <SafeIcon icon={FiCreditCard} size={10} />
                            {event.payment_method}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Invoices */}
      <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700/50">
          <h3 className="text-lg font-semibold text-white">Crypto Invoices</h3>
        </div>
        
        {data.invoices.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No invoices</div>
        ) : (
          <div className="divide-y divide-gray-700/30">
            {data.invoices.map((invoice) => (
              <div key={invoice.id} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-white">{invoice.plan_id} - ${invoice.amount_usd}</p>
                  <p className="text-xs text-gray-500">
                    {invoice.crypto_currency} {invoice.crypto_amount} • {formatDate(invoice.created_at)}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  invoice.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                  invoice.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {invoice.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserBillingTimeline;
